import { disconnect } from '@puzzlehq/sdk';
import { getNestedStringProperty } from '@puzzlehq/types';
import { useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useArcadeRecords } from './useArcadeRecords';

export const useArcadeTickets = (
  minAmount: number = 1,
  showToast: boolean = true,
  disconnectOnError: boolean = false
) => {
  const { partRecordsUnspent, partRecordsPending, loading, error } =
    useArcadeRecords();

  useEffect(() => {
    if (error) {
      console.error(error);
      showToast && toast.error(`Error fetching tickets: ${error}`);
      disconnectOnError && void disconnect();
    }
  }, [error]);

  const balanceUnspent = partRecordsUnspent
    ? partRecordsUnspent.reduce(
        (acc, record) =>
          acc +
          Number(
            getNestedStringProperty(record.data, ['amount'])?.replace(
              'u64.private',
              ''
            ) ?? 0
          ) /
            1_000_000,
        0
      )
    : undefined;

  const balancePending = partRecordsPending
    ? partRecordsPending.reduce(
        (acc, record) =>
          acc +
          Number(
            getNestedStringProperty(record.data, ['amount'])?.replace(
              'u64.private',
              ''
            ) ?? 0
          ) /
            1_000_000,
        0
      )
    : undefined;

  const descendingPartRecords = useMemo(() => {
    return partRecordsUnspent
      ?.slice()
      .filter((record) => {
        const val = Number(
          getNestedStringProperty(record.data, ['amount'])?.replace(
            'u64.private',
            ''
          ) ?? 0
        );

        return val >= minAmount;
      })
      .sort((a, b) => {
        const a_val = Number(
          getNestedStringProperty(a.data, ['amount'])?.replace(
            'u64.private',
            ''
          ) ?? 0
        );

        const b_val = Number(
          getNestedStringProperty(b.data, ['amount'])?.replace(
            'u64.private',
            ''
          ) ?? 0
        );

        return b_val - a_val;
      });
  }, [minAmount, JSON.stringify(partRecordsUnspent)]);

  const { largestRecordAmount, smallestFilteredRecord } = useMemo(() => {
    if (!descendingPartRecords?.length)
      return {
        largestRecordAmount: undefined,
        smallestFilteredRecord: undefined,
      };

    const largestRecordAmount = Number(
      getNestedStringProperty(descendingPartRecords[0].data, [
        'amount',
      ])?.replace('u64.private', '') ?? 0
    );

    const smallestFilteredRecord = descendingPartRecords.find((record) => {
      const value = Number(
        getNestedStringProperty(record.data, ['amount'])?.replace(
          'u64.private',
          ''
        ) ?? 0
      );

      if (value >= minAmount) return true;
      return false;
    });

    return {
      largestRecordAmount: largestRecordAmount / 1_000_000,
      smallestFilteredRecord,
    };
  }, [descendingPartRecords]);

  return {
    records: partRecordsUnspent,
    descendingPartRecords,
    largestPartRecord: descendingPartRecords?.[0],
    largestRecordAmount,
    smallestFilteredRecord,
    pendingCount: partRecordsPending?.length,
    unspentCount: partRecordsUnspent?.length,
    totalCount:
      partRecordsUnspent?.length !== undefined &&
      partRecordsPending?.length !== undefined
        ? partRecordsPending.length + partRecordsUnspent.length
        : undefined,
    loading,
    error,
    balance: balanceUnspent,
    balancePending,
    balanceTotal:
      balanceUnspent !== undefined && balancePending !== undefined
        ? balancePending + balanceUnspent
        : undefined,
  };
};
